import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  AutoAssignWorkOrderModal,
  MiBadge,
  MiButton,
  ZoomInput,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CALENDAR_ZOOM,
  PERMISSIONS,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import type { ICalendarWorkTemplateBase } from "@miplatform/types";
import { ButtonVariants, Colors } from "@miplatform/types";
import { setItemLocal } from "@miplatform/utils";
import { mdiChevronLeft, mdiFilterVariant, mdiFormatListGroup } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography, useTheme } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { CalendarFilterGroup } from "./CalendarFilterGroup";
import { EditTimeWindowsModal } from "./EditTimeWindowsModal/EditTimeWindowsModal";
import type {
  CalendarFilterGroupController,
  CalendarFilters,
} from "./useCalendarFilterGroup";

const ZOOM_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => ({
  label: `Calendar Zoom ${value * 100}%`,
  value,
}));

function countAppliedFilters(applied: CalendarFilters): number {
  return Object.values(applied).filter(Boolean).length;
}

export const CalendarToolBar = ({
  calendarSetId,
  zoom,
  setZoom,
  showCalendarFilters,
  toggleCalendarFilters,
  onBack,
  filters,
  appliedFilters,
  statusOptions,
  hasPendingFilters,
  onApplyFilters,
  onResetFilters,
  handleFilterChange,
  title,
  workTemplateBases = [],
}: CalendarToolBarProps) => {
  const theme = useTheme();
  const [
    isTimeWindowsModalOpen,
    handleTimeWindowsModalOpen,
    handleTimeWindowsModalClose,
  ] = useBoolean();
  const [
    isAutoAssignModalOpen,
    handleAutoAssignModalOpen,
    handleAutoAssignModalClose,
  ] = useBoolean();
  const { hasPermission } = usePermissions();

  const canViewTimeWindows = hasPermission(
    PERMISSIONS.WORK_ORDER_TEMPLATE.UPDATE_TIME_WINDOWS
  );

  const appliedCount = useMemo(
    () => countAppliedFilters(appliedFilters),
    [appliedFilters]
  );

  const { classes, cx } = useStyles();

  const persistZoomToLocalStorage = (zoom: number) => {
    setItemLocal(CALENDAR_ZOOM, zoom.toString());
    setZoom(zoom);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.topRow}>
        <Box>
          <Typography variant="HD2">{title}</Typography>
        </Box>
        <Box className={classes.calendarControls}>
          {onBack && (
            <MiButton
              variant={ButtonVariants.Outlined}
              color={Colors.Black}
              className={classes.baseButton}
              startIcon={{ path: mdiChevronLeft, iconSize: 0.75 }}
              title="Back"
              size="small"
              onClick={onBack}
            />
          )}
          <Box className={classes.zoomWrapper}>
            <ZoomInput
              value={zoom}
              options={ZOOM_OPTIONS}
              handleChange={(e: SelectChangeEvent<number>) =>
                persistZoomToLocalStorage(e.target.value)
              }
              className={classes.zoomInput}
            />
          </Box>
          <Box className={classes.filterToggleRow}>
            <MiButton
              onClick={toggleCalendarFilters}
              variant={ButtonVariants.Outlined}
              color={Colors.Black}
              className={cx(
                classes.baseButton,
                appliedCount > 0 && classes.filterButtonActive
              )}
              startIcon={{ path: mdiFilterVariant }}
              title="Filter Calendar"
            >
              {appliedCount > 0 && (
                <MiBadge
                  text={String(appliedCount)}
                  fontSize={11}
                  backgroundColor={theme.palette.teal.main}
                  rounded
                  className={classes.badge}
                />
              )}
            </MiButton>
            {canViewTimeWindows && (
              <MiButton
                onClick={handleTimeWindowsModalOpen}
                variant={BUTTON_VARIANTS.OUTLINED}
                color={BUTTON_COLORS.BLACK}
                className={classes.baseButton}
                title="Time Window"
                startIcon={{ path: mdiFormatListGroup }}
              />
            )}
          </Box>
        </Box>
      </Box>
      {showCalendarFilters && (
        <Box className={classes.filterGroup}>
          <CalendarFilterGroup
            filters={filters}
            statusOptions={statusOptions}
            hasPendingFilters={hasPendingFilters}
            onApplyFilters={onApplyFilters}
            onResetFilters={onResetFilters}
            onToggleFilters={toggleCalendarFilters}
            handleFilterChange={handleFilterChange}
          />
        </Box>
      )}
      <EditTimeWindowsModal
        open={isTimeWindowsModalOpen}
        onClose={handleTimeWindowsModalClose}
        onSuccess={handleAutoAssignModalOpen}
        workTemplateBases={workTemplateBases}
      />
      <AutoAssignWorkOrderModal
        open={isAutoAssignModalOpen}
        calendarSetId={calendarSetId}
        workBaseTemplates={[]}
        onClose={handleAutoAssignModalClose}
      />
    </Box>
  );
};

type CalendarToolBarProps = Pick<
  CalendarFilterGroupController,
  "filters" | "appliedFilters" | "statusOptions" | "handleFilterChange"
> & {
  title?: string;
  calendarSetId?: string;
  zoom: number;
  setZoom: (zoom: number) => void;
  showCalendarFilters: boolean;
  toggleCalendarFilters: () => void;
  hasPendingFilters: boolean;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  workTemplateBases?: ICalendarWorkTemplateBase[];
  onBack?: () => void;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing(2),
    margin: theme.spacing(0, 1),
    padding: theme.spacing(0, 2, 1, 2),
    backgroundColor: theme.palette.background.default,
  },
  topRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: theme.spacing(2),
    width: "100%",
  },
  calendarControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  filterToggleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  baseButton: {
    height: 24,
    fontSize: 12,
    borderColor: theme.palette.grey.darkAccent,
    borderRadius: 2,
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  filterButtonActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  badge: {
    padding: theme.spacing(0, 1),
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  zoomWrapper: {
    flexShrink: 0,
  },
  zoomInput: {
    "& .MuiSelect-select": {
      fontSize: theme.fontSize.small,
    },
  },
}));
